import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Columna de la tabla del turno. */
export interface ColumnaDelTurno<T> {
  encabezado: string;
  /** Alineación y ancho de la celda. */
  clase?: string;
  celda: (fila: T) => ReactNode;
}

interface Props<T> {
  titulo: string;
  descripcion: string;
  columnas: ColumnaDelTurno<T>[];
  filas: T[];
  total: number;
  pagina: number;
  tamanioPagina: number;
  buscar: string;
  onBuscar: (valor: string) => void;
  onPagina: (pagina: number) => void;
  cargando: boolean;
  vacio: string;
  /** Aviso de que el turno no está abierto. */
  sinTurno: boolean;
  /** Qué verá el operador cuando no tenga turno abierto. */
  avisoSinTurno: string;
  /** Acciones de la cabecera (por ejemplo, abrir los métodos de pago). */
  acciones?: ReactNode;
}

/**
 * Tabla de un listado del turno (documentos, notas de venta y vales).
 *
 * Los tres listados comparten la misma forma: buscador, tabla y paginado contra el
 * servidor, con la fila "no tienes turno abierto" cuando corresponde.
 */
export function TablaDelTurno<T>({
  titulo,
  descripcion,
  columnas,
  filas,
  total,
  pagina,
  tamanioPagina,
  buscar,
  onBuscar,
  onPagina,
  cargando,
  vacio,
  sinTurno,
  avisoSinTurno,
  acciones,
}: Props<T>) {
  /**
   * El buscador espera al Enter (o al botón) para no lanzar una consulta por tecla:
   * el valor aplicado es siempre el que llega por `buscar`.
   */
  const [texto, setTexto] = useState(buscar);
  const paginas = total > 0 ? Math.ceil(total / tamanioPagina) : 1;

  return (
    <SectionCard
      title={titulo}
      description={descripcion}
      action={acciones ? <div className="flex flex-wrap items-center gap-2">{acciones}</div> : null}
    >
      {sinTurno ? (
        <p className="text-sm text-ink-500">
          No tienes un turno abierto: abre tu turno para ver {avisoSinTurno}.
        </p>
      ) : (
        <div className="space-y-4">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(evento) => {
              evento.preventDefault();
              onBuscar(texto.trim());
            }}
          >
            <Input
              className="max-w-xs"
              placeholder="Buscar por cliente, placa o número"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
            />
            <Button
              variant="outline"
              size="sm"
              type="submit"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
            >
              Buscar
            </Button>
            {buscar !== '' ? (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  setTexto('');
                  onBuscar('');
                }}
              >
                Limpiar
              </Button>
            ) : null}
            <span className="text-xs text-ink-400">
              {total} registro{total === 1 ? '' : 's'}
            </span>
          </form>

          {cargando ? (
            <Skeleton className="h-32 w-full" />
          ) : filas.length === 0 ? (
            <p className="text-sm text-ink-500">{vacio}</p>
          ) : (
            <div className="overflow-x-auto rounded-control border border-ink-100">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    {columnas.map((columna) => (
                      <th
                        key={columna.encabezado}
                        className={cn('px-3 py-2 text-left font-semibold', columna.clase)}
                      >
                        {columna.encabezado}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila, indice) => (
                    <tr key={indice} className="border-t border-ink-100">
                      {columnas.map((columna) => (
                        <td
                          key={columna.encabezado}
                          className={cn('px-3 py-2 align-middle text-ink-700', columna.clase)}
                        >
                          {columna.celda(fila)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {paginas > 1 ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-ink-500">
                Página {pagina} de {paginas}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => onPagina(pagina - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => onPagina(pagina + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}
